import React, { useEffect, useMemo, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";

import { ErrorMessage, Formik } from "formik";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, FormGroup, Form, Label, Input } from "reactstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";
import ReactTableGenericForUsers from "Components/ReactTableGenericForUsers";
import { Link } from "react-router-dom";
import { useBesturen, useSaveBestuur, useUpdateBestuur } from "Api/Admin/Besturen";
import { debounce } from "helperFunctions";

const validationSchema = Yup.object().shape({
    Naam: Yup.string().required("Veld mag niet leeg zijn"),
});

const BesturenTable = ({ toggle, openModal, editSelectedRow, setEditSelectedRow }) => {
    const [pageSizeCount, setPageSizeCount] = useState(20);
    const [pageNumber, setPageNumber] = useState(1);
    const [search, setSearch] = useState("");
    const [disableModalSubmitButton, setDisableModalSubmitButton] = useState(true);

    const paginate = (val) => {
        setPageNumber(val);
    };

    const {
        data: response,
        isLoading,
        mutate,
    } = useBesturen(pageSizeCount, pageNumber, search);

    const { trigger: saveBestuur } = useSaveBestuur();
    const { trigger: updateBestuur } = useUpdateBestuur();

    const data = response?.data?.communitiesLists || [];
    const totalPageCount = response?.data?.paginate || 0;
    const totalCount = response?.data?.totalCount;
    const communityList =
        response?.data?.communitiesLists?.map((item) => ({
            value: item.iCommunityId,
            label: item.vCommunityName,
        })) || [];

    useEffect(() => {
        if (!openModal) {
            setEditSelectedRow(null);
            setDisableModalSubmitButton(true);
        }
    }, [openModal, setEditSelectedRow]);

    const indexPageSize = (val) => {
        setPageSizeCount(val);
    };

    const functieTablePageSize = (val) => {
        setPageNumber(1);
        indexPageSize(val);
    };

    const userTablePageSize = (val) => {
        setPageNumber(1);
        indexPageSize(val);
    };

    // Stable debounce — recreating it each render leaves orphaned timers and can
    // cascade into React error #185 (max update depth) when navigating away.
    const handleInputChange = useMemo(
        () =>
            debounce((val) => {
                setPageNumber(1);
                setSearch(val);
            }, 500),
        [],
    );

    const filterList = (val) => {
        if (val === search) {
            return;
        }
        handleInputChange(val);
    };

    const formSubmitHandler = (values) => {
        const isSave = editSelectedRow?.type === "save";
        const payload = isSave
            ? { vCommunityName: values.Naam, eStatus: "Active" }
            : {
                  iCommunityId: editSelectedRow?.value?.iCommunityId,
                  vCommunityName: values.Naam,
                  eStatus: "Active",
              };

        const triggerFn = isSave ? saveBestuur : updateBestuur;

        triggerFn({
            data: payload,
        })
            .then((response) => {
                if (response?.status) {
                    toggle();
                    mutate();
                    toast.success(isSave ? "Opgeslagen" : "Bijgewerkt");
                } else {
                    toast.error(isSave ? "Kan niet opslaan." : "Kan niet updaten.");
                }
            })
            .catch((error) => {
                console.error(error);
                toast.error("Er is iets misgegaan");
            });
    };

    const columns = [
        {
            header: "Bestuur",
            accessorKey: "vCommunityName",
            cell: (value) => {
                const row = value.row.original;
                return (
                    <Link
                        className="svg-pointer"
                        to={`/beheerder/besturen/${row.iCommunityId}`}
                        state={{ data: row, communityList }}
                    >
                        {row.vCommunityName}
                    </Link>
                );
            },
        },
        {
            header: "Regio",
            accessorKey: "regio",
        },
    ];

    const formDataState = {
        Naam: editSelectedRow?.value?.vCommunityName || "",
    };

    return (
        <>
            <ReactTableGenericForUsers
                data={data || []}
                columns={columns}
                setPageNumber={setPageNumber}
                pageNumber={pageNumber}
                totalPageCount={totalPageCount}
                paginate={paginate}
                functieTablePageSize={functieTablePageSize}
                pageSizeCount={pageSizeCount}
                showLoader={isLoading}
                tableClassName="mb-0 table table-borderless table-sm table-hover table-heading-style"
                filters={{ search: true }}
                userTablePageSize={userTablePageSize}
                count={totalCount}
                filterList={filterList}
            />

            <Modal
                isOpen={openModal}
                toggle={toggle}
                className="feedback-modal-style overig-borderen-modal-style modal-centered link-form"
            >
                <ModalHeader toggle={toggle}>Bestuur toevoegen</ModalHeader>
                <ModalBody>
                    <Formik
                        enableReinitialize
                        initialValues={formDataState}
                        validationSchema={validationSchema}
                        onSubmit={formSubmitHandler}
                    >
                        {({ values, handleChange, handleSubmit }) => (
                            <Form onSubmit={handleSubmit}>
                                <FormGroup className="d-flex justify-content-between">
                                    <Label for="Naam" className="mt-2">
                                        Naam
                                    </Label>

                                    <div style={{ width: "60%" }}>
                                        <Input
                                            name="Naam"
                                            type="text"
                                            value={values.Naam}
                                            onChange={(e) => {
                                                handleChange(e);
                                                setDisableModalSubmitButton(false);
                                                const isSameName =
                                                    editSelectedRow?.value?.vCommunityName ===
                                                    e.target.value;
                                                const isEmpty = e.target.value === "";
                                                setDisableModalSubmitButton(isEmpty || isSameName);
                                                if (isSameName) {
                                                    toast.error("Naam bestaat al.");
                                                }
                                            }}
                                        />
                                        <ErrorMessage
                                            name="Naam"
                                            component="small"
                                            className="block mt-2 invalid-feedback"
                                        />
                                    </div>
                                </FormGroup>
                                <ModalFooter className="d-flex align-items-center justify-content-center border-bottom-0 py-4 mt-4">
                                    <Button
                                        className="btn-color-style opslaan-btn"
                                        size="sm"
                                        outline
                                        color="primary"
                                        type="submit"
                                        disabled={disableModalSubmitButton}
                                    >
                                        Toepassen
                                    </Button>
                                    <Button
                                        className="btn-color-style opslaan-btn"
                                        size="sm"
                                        outline
                                        color="primary"
                                        onClick={toggle}
                                    >
                                        Annuleren
                                    </Button>
                                </ModalFooter>
                            </Form>
                        )}
                    </Formik>
                </ModalBody>
            </Modal>
        </>
    );
};

export default BesturenTable;
